import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { ContactForm } from '../components/ContactForm';
import { Footer } from '../components/Footer';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Toaster } from 'sonner';

const contactInfo = [
  {
    icon: Mail,
    title: 'E-Mail',
    content: 'hey@mehrazav.de',
    href: 'mailto:hey@mehrazav.de'
  },
  {
    icon: Phone,
    title: 'Telefon',
    content: '+49 176 7040 1954',
    href: 'tel:+4917670401954'
  },
  {
    icon: MapPin,
    title: 'Adresse',
    content: 'Zum Großen Zernsee 2H, 14542 Werder (Havel)',
    href: null
  },
  {
    icon: Clock,
    title: 'Erreichbarkeit',
    content: 'Mo – Fr: 9:00 – 18:00 Uhr',
    href: null
  }
];

const Kontakt = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="kontakt-page">
      <Navbar />
      {/* Hero glow that extends behind navbar */}
      <div className="hero-glow" />
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#121212',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff'
          }
        }} 
      />
      <main className="pt-20 relative">
        {/* Header */}
        <section className="py-12 md:py-16 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
              <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                Kontakt
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold tracking-tight leading-none mb-6" data-testid="kontakt-headline">
                Kostenlose{' '}
                <span className="text-primary">Ersteinschätzung</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Schildern Sie uns kurz Ihre Situation. Wir melden uns in der Regel taggleich bei Ihnen.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 md:py-16 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-1"
              >
                <h2 className="text-2xl font-heading font-semibold text-white mb-8">
                  Kontaktinformationen
                </h2>
                <div className="flex flex-col gap-6">
                  {contactInfo.map((info, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-4"
                      data-testid={`contact-info-${index}`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">{info.title}</div>
                        {info.href ? (
                          <a 
                            href={info.href}
                            className="text-white hover:text-primary transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <span className="text-white">{info.content}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Info Box */}
                <div className="mt-12 p-6 bg-card/30 border border-white/10 rounded-2xl">
                  <h3 className="text-white font-heading font-semibold mb-3">Was Sie erwartet</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      Antwort innerhalb von 24 Stunden
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      Erste Einschätzung Ihrer Situation
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      Konkreter Fahrplan für Ihr Vorhaben
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      Unverbindlich und kostenlos
                    </li>
                  </ul>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-2"
              >
                <div className="bg-card/30 border border-white/10 rounded-2xl p-8 md:p-10">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                    Anfrage stellen
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    Beschreiben Sie kurz Ihre Situation und Ihr Anliegen.
                  </p>
                  <ContactForm />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default Kontakt;
